/**
 * Date Range Picker Component
 * Professional date picker using react-datepicker
 * Follows SaaS industry standards for date selection
 */

import React, { useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { startOfDay } from "date-fns";
import { CalendarIcon, XMarkIcon } from "@heroicons/react/24/outline";

// Start/End time options at 15-minute increments (#7: the native <input type="time"> spinner was
// finicky to click — "clicking the to box but not really"). A plain <select> is a big, reliable
// click target with a clean dropdown. value = "HH:MM" 24-hour (identical format to the old input,
// so nothing downstream changes); label = friendly 12-hour AM/PM.
const TIME_OPTIONS: { value: string; label: string }[] = (() => {
  const out: { value: string; label: string }[] = [];
  for (let h = 0; h < 24; h += 1) {
    for (let m = 0; m < 60; m += 15) {
      const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      const h12 = h % 12 === 0 ? 12 : h % 12;
      const ampm = h < 12 ? "AM" : "PM";
      out.push({
        value,
        label: `${h12}:${String(m).padStart(2, "0")} ${ampm}`,
      });
    }
  }
  return out;
})();

// Keep an off-grid existing time (e.g. an event previously saved as 10:05) selectable so editing it
// never silently drops the value — it's prepended as its own option.
export function timeSelectOptions(
  current?: string,
): { value: string; label: string }[] {
  if (current && !TIME_OPTIONS.some((t) => t.value === current)) {
    const [hh, mm] = current.split(":");
    const h = Number(hh);
    const h12 = h % 12 === 0 ? 12 : h % 12;
    const ampm = h < 12 ? "AM" : "PM";
    return [{ value: current, label: `${h12}:${mm} ${ampm}` }, ...TIME_OPTIONS];
  }
  return TIME_OPTIONS;
}

export interface DateRange {
  startDate: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  allDay?: boolean;
}

export interface DateValidationErrors {
  startDate?: string;
  endDate?: string;
  dateRange?: string;
}

/**
 * Check if a date is in the past (before today)
 */
export function isDateInPast(date: Date): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const compareDate = new Date(date);
  compareDate.setHours(0, 0, 0, 0);
  return compareDate < today;
}

/**
 * Validate the entire date range
 */
export function validateDateRange(dateRange: DateRange): DateValidationErrors {
  const errors: DateValidationErrors = {};

  // Parse dates as local (not UTC) to avoid timezone off-by-one
  const startDate = dateRange.startDate
    ? stringToDate(dateRange.startDate)
    : null;
  const endDate = dateRange.endDate ? stringToDate(dateRange.endDate) : null;

  // Validate start date is not in the past
  if (startDate && isDateInPast(startDate)) {
    errors.startDate = "Start date cannot be in the past.";
  }

  // Validate end date is after start date
  if (startDate && endDate) {
    if (endDate < startDate) {
      errors.dateRange = "End date must be on or after the start date.";
    }

    // If same day, check times
    if (
      dateRange.endDate === dateRange.startDate &&
      dateRange.startTime &&
      dateRange.endTime &&
      !dateRange.allDay
    ) {
      if (dateRange.endTime < dateRange.startTime) {
        errors.dateRange = "End time must be after start time on the same day.";
      }
    }
  }

  // No end date = the event ends on its start day (the create wizard already saves it that way). Then the end time
  // must come after the start time; otherwise the save would send an end equal to or before the start and the
  // database refuses it ("Failed to save event"). MSR activation fix 5 (QA test 2026-10-10).
  if (
    startDate &&
    !endDate &&
    !dateRange.allDay &&
    dateRange.startTime &&
    dateRange.endTime &&
    dateRange.endTime <= dateRange.startTime
  ) {
    errors.dateRange =
      "End time must be after the start time. With no end date, the event ends on its start day — pick an end date for an event that runs past midnight or over several days.";
  }

  return errors;
}

/**
 * Convert Date object to YYYY-MM-DD string
 */
function dateToString(date: Date | null): string {
  if (!date) {
    return "";
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Parse YYYY-MM-DD string to Date object
 */
function stringToDate(dateStr: string): Date | null {
  if (!dateStr) {
    return null;
  }
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Format date for display (e.g., "Jan 15, 2026")
 */
function formatDisplayDate(date: Date | null): string {
  if (!date) {
    return "";
  }
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (dateRange: DateRange) => void;
  required?: boolean;
  disabled?: boolean;
  showValidation?: boolean;
  /** When true, allows past dates (for editing existing events) */
  allowPastDates?: boolean;
  /**
   * When true, the End Date trigger (and its calendar/clear affordance) is
   * DISABLED — for single-day event types whose end date is coerced to equal
   * the start date, so the operator can't open a control whose selection is
   * silently discarded. Opt-in; default false keeps every other consumer
   * (EventForm's edit path, etc.) unchanged. Only the end DATE is locked — the
   * end TIME stays editable. */
  lockEndDate?: boolean;
  /**
   * Consumer-supplied required-field errors, associated with the matching control
   * via aria-invalid + aria-describedby and shown field-level (e.g. the wizard's
   * "Choose a start date." / "Set a start time."). Opt-in; omitting it (EventForm
   * and every other consumer) renders exactly as before. Cross-field ordering
   * errors are still handled by this component's internal validation. */
  externalErrors?: {
    startDate?: string;
    startTime?: string;
    endDate?: string;
    endTime?: string;
  };
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  required = false,
  disabled = false,
  showValidation = true,
  allowPastDates = false,
  lockEndDate = false,
  externalErrors = {},
}) => {
  // The end DATE trigger is disabled if the whole picker is disabled OR the end
  // date is locked to the start date for a single-day type.
  const endDateDisabled = disabled || lockEndDate;
  // Parse string dates to Date objects
  const startDate = stringToDate(value.startDate);
  const endDate = stringToDate(value.endDate || "");

  // Validate dates
  const validationErrors = useMemo(() => {
    if (!showValidation) {
      return {};
    }
    const errors = validateDateRange(value);
    // Skip past date validation when editing existing events
    if (allowPastDates && errors.startDate?.includes("past")) {
      delete errors.startDate;
    }
    return errors;
  }, [value, showValidation, allowPastDates]);

  const handleStartDateChange = (date: Date | null) => {
    const dateStr = dateToString(date);
    onChange({
      ...value,
      startDate: dateStr,
      // If end date is before new start date, clear it
      endDate:
        value.endDate && date && stringToDate(value.endDate)! < date
          ? ""
          : value.endDate,
    });
  };

  const handleEndDateChange = (date: Date | null) => {
    onChange({
      ...value,
      endDate: dateToString(date),
    });
  };

  const handleStartTimeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({
      ...value,
      startTime: e.target.value,
    });
  };

  const handleEndTimeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({
      ...value,
      endTime: e.target.value,
    });
  };

  const handleAllDayToggle = () => {
    const newAllDay = !value.allDay;
    onChange({
      ...value,
      allDay: newAllDay,
      startTime: newAllDay ? undefined : value.startTime,
      endTime: newAllDay ? undefined : value.endTime,
    });
  };

  const clearStartDate = () => {
    onChange({
      ...value,
      startDate: "",
      startTime: undefined,
      // Also clear end date if start is cleared
      endDate: "",
      endTime: undefined,
    });
  };

  const clearEndDate = () => {
    onChange({
      ...value,
      endDate: "",
      endTime: undefined,
    });
  };

  // Custom input component for consistent styling
  const CustomInput = React.forwardRef<
    HTMLButtonElement,
    {
      value?: string;
      onClick?: () => void;
      placeholder?: string;
      hasError?: boolean;
      label: string;
      required?: boolean;
      onClear?: () => void;
      showClear?: boolean;
      /** Per-field disable override; falls back to the component-level `disabled`. */
      fieldDisabled?: boolean;
      /** id of the field-level error message, for aria-describedby. */
      ariaDescribedBy?: string;
    }
  >(
    (
      {
        value,
        onClick,
        placeholder,
        hasError,
        label,
        required: isRequired,
        onClear,
        showClear,
        fieldDisabled,
        ariaDescribedBy,
      },
      ref,
    ) => {
      const isDisabled = fieldDisabled ?? disabled;
      return (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label} {isRequired && <span className="text-red-500">*</span>}
          </label>
          <div className="relative">
            <button
              type="button"
              ref={ref}
              onClick={onClick}
              disabled={isDisabled}
              aria-invalid={hasError || undefined}
              aria-describedby={hasError ? ariaDescribedBy : undefined}
              className={`w-full px-3 py-2 text-left border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white ${
                showClear ? "pr-16" : "pr-10"
              } ${
                hasError
                  ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                  : "border-gray-300"
              } ${isDisabled ? "bg-gray-100 cursor-not-allowed" : "cursor-pointer hover:border-gray-400"}`}
            >
              {value || <span className="text-gray-400">{placeholder}</span>}
            </button>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-2">
              {showClear && value && onClear && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClear();
                  }}
                  className="p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 z-10 bg-white"
                  title="Clear date"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
              <CalendarIcon className="h-5 w-5 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>
      );
    },
  );
  CustomInput.displayName = "CustomDateInput";

  return (
    <div className="space-y-4 relative z-40">
      {/* All Day Toggle */}
      <div className="flex items-center">
        <input
          type="checkbox"
          id="allDay"
          checked={value.allDay || false}
          onChange={handleAllDayToggle}
          disabled={disabled}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
        />
        <label
          htmlFor="allDay"
          className="ml-2 block text-sm text-gray-900 cursor-pointer"
        >
          All Day Event
        </label>
      </div>

      {/* Start Date & Time */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <DatePicker
            selected={startDate}
            onChange={handleStartDateChange}
            minDate={allowPastDates ? undefined : startOfDay(new Date())}
            dateFormat="MMM d, yyyy"
            placeholderText="Select start date"
            disabled={disabled}
            showPopperArrow={false}
            popperPlacement="bottom-start"
            customInput={
              <CustomInput
                label="Start Date"
                required={required}
                hasError={
                  !!validationErrors.startDate || !!externalErrors.startDate
                }
                ariaDescribedBy="drp-start-date-error"
                placeholder="Select start date"
                showClear={!!startDate}
                onClear={clearStartDate}
              />
            }
            calendarClassName="shadow-lg border border-gray-200 rounded-lg"
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            todayButton="Today"
          />
          {(validationErrors.startDate || externalErrors.startDate) && (
            <p id="drp-start-date-error" className="mt-1 text-sm text-red-600">
              {validationErrors.startDate || externalErrors.startDate}
            </p>
          )}
        </div>
        {!value.allDay && (
          <div>
            <label
              htmlFor="drp-start-time"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Start Time
            </label>
            <select
              id="drp-start-time"
              value={value.startTime || ""}
              onChange={handleStartTimeChange}
              disabled={disabled}
              aria-invalid={!!externalErrors.startTime || undefined}
              aria-describedby={
                externalErrors.startTime ? "drp-start-time-error" : undefined
              }
              className={`w-full px-3 py-2 border rounded-md bg-white focus:ring-primary-500 focus:border-primary-500 ${
                externalErrors.startTime ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Select a time…</option>
              {timeSelectOptions(value.startTime).map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            {externalErrors.startTime && (
              <p
                id="drp-start-time-error"
                className="mt-1 text-sm text-red-600"
              >
                {externalErrors.startTime}
              </p>
            )}
          </div>
        )}
      </div>

      {/* End Date & Time */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <DatePicker
            selected={endDate}
            onChange={handleEndDateChange}
            minDate={startDate || startOfDay(new Date())}
            dateFormat="MMM d, yyyy"
            placeholderText="Select end date (optional)"
            disabled={endDateDisabled}
            showPopperArrow={false}
            popperPlacement="bottom-start"
            customInput={
              <CustomInput
                label="End Date"
                hasError={
                  !!validationErrors.endDate || !!externalErrors.endDate
                }
                ariaDescribedBy="drp-end-date-error"
                placeholder="Same as start date"
                showClear={!!endDate && !lockEndDate}
                onClear={clearEndDate}
                fieldDisabled={endDateDisabled}
              />
            }
            calendarClassName="shadow-lg border border-gray-200 rounded-lg"
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            todayButton="Today"
          />
          {validationErrors.endDate || externalErrors.endDate ? (
            <p id="drp-end-date-error" className="mt-1 text-sm text-red-600">
              {validationErrors.endDate || externalErrors.endDate}
            </p>
          ) : lockEndDate ? (
            <p className="mt-1 text-sm text-gray-500">
              Locked to the start date for this single-day event type.
            </p>
          ) : (
            <p className="mt-1 text-sm text-gray-500">
              Leave empty if same as start date
            </p>
          )}
        </div>
        {!value.allDay && (
          <div>
            <label
              htmlFor="drp-end-time"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              End Time
            </label>
            <select
              id="drp-end-time"
              value={value.endTime || ""}
              onChange={handleEndTimeChange}
              disabled={disabled}
              aria-invalid={!!externalErrors.endTime || undefined}
              aria-describedby={
                externalErrors.endTime ? "drp-end-time-error" : undefined
              }
              className={`w-full px-3 py-2 border rounded-md bg-white focus:ring-primary-500 focus:border-primary-500 ${
                externalErrors.endTime ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Select a time…</option>
              {timeSelectOptions(value.endTime).map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            {externalErrors.endTime ? (
              <p id="drp-end-time-error" className="mt-1 text-sm text-red-600">
                {externalErrors.endTime}
              </p>
            ) : (
              <p className="mt-1 text-sm text-gray-500">
                Leave empty if same as start time
              </p>
            )}
          </div>
        )}
      </div>

      {/* Date Range Error */}
      {validationErrors.dateRange && (
        <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md border border-red-200">
          {validationErrors.dateRange}
        </div>
      )}

      {/* Date Range Summary */}
      {value.startDate && (
        <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md border border-gray-200">
          <strong>Selected:</strong> {formatDisplayDate(startDate)}
          {!value.allDay && value.startTime && ` at ${value.startTime}`}
          {endDate && value.endDate !== value.startDate && (
            <>
              {" → "}
              {formatDisplayDate(endDate)}
              {!value.allDay && value.endTime && ` at ${value.endTime}`}
            </>
          )}
          {value.allDay && " (All Day)"}
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
